import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Plus, Trash2 } from 'lucide-react';

function uid() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/**
 * Multi-day editor used in Event create/edit dialogs.
 *
 * Props:
 *   - days: current value, list of {id, date, start_time, end_time, label?}
 *   - onChange(days)
 *   - fallback: {date, start_time, end_time} from the parent's single-day fields
 */
export default function EventDaysEditor({ days, onChange, fallback }) {
  const { t } = useTranslation();
  const isMulti = Array.isArray(days) && days.length > 0;
  const [enabled, setEnabled] = useState(isMulti);

  useEffect(() => {
    setEnabled(isMulti);
  }, [isMulti]);

  const toggle = (next) => {
    setEnabled(next);
    if (next) {
      // Initialize from fallback (first day = current single-day fields).
      onChange([
        { id: uid(), date: fallback?.date || '', start_time: fallback?.start_time || '', end_time: fallback?.end_time || '', label: '' },
      ]);
    } else {
      onChange([]); // back to single-day mode
    }
  };

  const update = (idx, patch) => {
    const next = days.map((d, i) => (i === idx ? { ...d, ...patch } : d));
    onChange(next);
  };

  const remove = (idx) => {
    if (days.length <= 1) return;
    onChange(days.filter((_, i) => i !== idx));
  };

  const add = () => {
    const last = days[days.length - 1];
    onChange([
      ...days,
      {
        id: uid(),
        date: last?.date || fallback?.date || '',
        start_time: last?.start_time || fallback?.start_time || '',
        end_time: last?.end_time || fallback?.end_time || '',
        label: '',
      },
    ]);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="font-medium">{t('events.multiDay')}</Label>
        <Switch checked={enabled} onCheckedChange={toggle} />
      </div>

      {enabled && (
        <div className="space-y-2 rounded-md border border-slate-200 p-3 bg-slate-50/60">
          {(days || []).map((d, idx) => (
            <div key={d.id} className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-3">
                {idx === 0 && <Label className="text-xs">{t('events.dayDate')}</Label>}
                <Input type="date" value={d.date}
                  onChange={(e) => update(idx, { date: e.target.value })} required />
              </div>
              <div className="col-span-2">
                {idx === 0 && <Label className="text-xs">{t('events.dayStart')}</Label>}
                <Input type="time" value={d.start_time}
                  onChange={(e) => update(idx, { start_time: e.target.value })} required />
              </div>
              <div className="col-span-2">
                {idx === 0 && <Label className="text-xs">{t('events.dayEnd')}</Label>}
                <Input type="time" value={d.end_time}
                  onChange={(e) => update(idx, { end_time: e.target.value })} required />
              </div>
              <div className="col-span-4">
                {idx === 0 && <Label className="text-xs">{t('events.dayLabel')}</Label>}
                <Input value={d.label || ''}
                  placeholder={`${t('events.day')} ${idx + 1}`}
                  onChange={(e) => update(idx, { label: e.target.value })} />
              </div>
              <div className="col-span-1 flex justify-end">
                <Button type="button" size="sm" variant="ghost"
                  disabled={days.length <= 1}
                  onClick={() => remove(idx)}
                  title={t('events.removeDay')}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </div>
          ))}
          <Button type="button" size="sm" variant="outline" onClick={add}>
            <Plus className="w-4 h-4 mr-1" />
            {t('events.addDay')}
          </Button>
        </div>
      )}
    </div>
  );
}
